/**
 * Region batching for chunk meshes: draw-call reduction.
 *
 * Every chunk used to be its own THREE.Mesh per pass (plus a shadow twin), so
 * a fancy-preset view issued ~450 draw calls a frame and the main thread was
 * bound on per-draw overhead (uniform uploads, culling, VAO binds).
 *
 * Here REGION x REGION chunks share one GPU vertex buffer per pass and draw
 * with a single call. Chunk geometry is written straight into its slot with
 * bufferSubData (the worker's arrays are dropped afterwards, nothing is kept
 * on the CPU — writes are staged and copied GPU-side); a remesh that still fits rewrites its slot in place, otherwise
 * the chunk moves to a fresh slot and the old one is zeroed (degenerate
 * quads). Buffers grow / compact GPU-side with copyBufferSubData.
 *
 * Positions stay in the mesher's packed (local + 1) * 256 format: we add the
 * chunk's offset inside the region (<= 3 * 16 blocks, still < 65536) before
 * upload, so the chunk shaders are unchanged and the mesh's position is the
 * region origin. Indices are the fixed quad pattern, so one shared index
 * buffer serves every region.
 */

import * as THREE from 'three';
import { CHUNK_SIZE } from '../constants';
import { POS_SCALE, type PassMesh } from '../mesher/greedy';

/** Chunks per region side. 4 -> 64x64-block regions (any value <= 15 fits the u16 positions). */
export const REGION = 4;

/** Vertex layout: a = u16x4, b = u16x4, c = u8x4 (normalized). */
const A_BYTES = 8, B_BYTES = 8, C_BYTES = 4;

interface Slot { start: number; cap: number; count: number; minY: number; maxY: number; lx: number; lz: number; dist: number }

/** Shared quad index buffer (v, v+1, v+2, v, v+2, v+3 per quad). */
class QuadIndex {
  buffer: WebGLBuffer | null = null;
  attr: THREE.GLBufferAttribute | null = null;
  maxVerts = 0;
  constructor(private readonly gl: WebGL2RenderingContext) {}

  /** Ensure capacity; returns true if the attribute object changed. */
  ensure(verts: number): boolean {
    if (verts <= this.maxVerts && this.attr) return false;
    const gl = this.gl;
    let n = Math.max(65536, this.maxVerts);
    while (n < verts) n *= 2;
    const quads = n >> 2;
    const idx = new Uint32Array(quads * 6);
    for (let q = 0, i = 0, v = 0; q < quads; q++, i += 6, v += 4) {
      idx[i] = v; idx[i + 1] = v + 1; idx[i + 2] = v + 2;
      idx[i + 3] = v; idx[i + 4] = v + 2; idx[i + 5] = v + 3;
    }
    // ELEMENT_ARRAY_BUFFER binding is VAO state: unbind whatever three has
    // bound, upload, then restore it so three's VAO cache stays truthful.
    const prevVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null;
    gl.bindVertexArray(null);
    if (this.buffer) gl.deleteBuffer(this.buffer);
    this.buffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    gl.bindVertexArray(prevVao);
    this.maxVerts = n;
    this.attr = new THREE.GLBufferAttribute(this.buffer, gl.UNSIGNED_INT, 1, 4, quads * 6);
    return true;
  }
}

/** One pass of one region: GPU buffers + slot allocator + the drawn mesh(es). */
class RegionPass {
  cap = 0; // vertices
  end = 0; // high-water mark (vertices)
  live = 0; // vertices owned by chunks (capacity)
  bufA: WebGLBuffer | null = null;
  bufB: WebGLBuffer | null = null;
  bufC: WebGLBuffer | null = null;
  readonly slots = new Map<number, Slot>();
  free: Array<{ start: number; cap: number }> = [];
  readonly geometry = new THREE.BufferGeometry();
  readonly meshes: THREE.Mesh[] = [];

  constructor(private readonly gl: WebGL2RenderingContext, private readonly index: QuadIndex) {
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1);
  }

  private bindAttrs(): void {
    const gl = this.gl;
    const g = this.geometry as unknown as { setAttribute(n: string, a: THREE.GLBufferAttribute): void };
    // fresh attribute objects so three's VAO cache rebinds the new buffers
    g.setAttribute('apos', new THREE.GLBufferAttribute(this.bufA!, gl.UNSIGNED_SHORT, 4, 2, this.cap));
    g.setAttribute('auv', new THREE.GLBufferAttribute(this.bufB!, gl.UNSIGNED_SHORT, 4, 2, this.cap));
    g.setAttribute('acolor', new THREE.GLBufferAttribute(this.bufC!, gl.UNSIGNED_BYTE, 4, 1, this.cap, true));
    this.geometry.setIndex(this.index.attr as unknown as THREE.BufferAttribute);
  }

  /** Re-point at the shared index buffer after it grew. */
  refreshIndex(): void {
    if (this.bufA) this.geometry.setIndex(this.index.attr as unknown as THREE.BufferAttribute);
  }

  /** (Re)allocate buffers of `cap` vertices, copying live slots compactly. */
  private realloc(cap: number): void {
    const gl = this.gl;
    const na = gl.createBuffer()!, nb = gl.createBuffer()!, nc = gl.createBuffer()!;
    for (const [buf, bytes] of [[na, A_BYTES], [nb, B_BYTES], [nc, C_BYTES]] as const) {
      gl.bindBuffer(gl.COPY_WRITE_BUFFER, buf);
      gl.bufferData(gl.COPY_WRITE_BUFFER, cap * bytes, gl.DYNAMIC_DRAW); // zero-initialised
    }
    let w = 0;
    if (this.bufA) {
      const olds = [this.bufA, this.bufB!, this.bufC!];
      const news = [na, nb, nc];
      const sizes = [A_BYTES, B_BYTES, C_BYTES];
      // compact: live slots packed in start order
      const live = [...this.slots.values()].sort((x, y) => x.start - y.start);
      for (let k = 0; k < 3; k++) {
        gl.bindBuffer(gl.COPY_READ_BUFFER, olds[k]);
        gl.bindBuffer(gl.COPY_WRITE_BUFFER, news[k]);
        let ww = 0;
        for (const s of live) {
          if (s.count > 0) gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, s.start * sizes[k], ww * sizes[k], s.count * sizes[k]);
          ww += s.cap;
        }
        gl.deleteBuffer(olds[k]);
      }
      for (const s of live) { s.start = w; w += s.cap; }
      this.sorted = null;
      gl.bindBuffer(gl.COPY_READ_BUFFER, null);
    }
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
    this.bufA = na; this.bufB = nb; this.bufC = nc;
    this.cap = cap;
    this.end = w;
    this.free = [];
    if (this.index.ensure(cap)) this.onIndexGrow?.();
    this.bindAttrs();
  }

  onIndexGrow: (() => void) | null = null;

  private alloc(cap: number): number {
    for (let i = 0; i < this.free.length; i++) {
      const f = this.free[i];
      if (f.cap >= cap) {
        const start = f.start;
        if (f.cap - cap >= 64) { f.start += cap; f.cap -= cap; } else { this.free.splice(i, 1); cap = f.cap; }
        this.lastCap = cap;
        return start;
      }
    }
    if (this.end + cap > this.cap) {
      const need = this.live + cap;
      let n = Math.max(16384, this.cap);
      // compact if at least half is garbage, else grow
      if (need * 2 > n) { while (n < need * 1.5) n *= 2; }
      this.realloc(n);
    }
    const start = this.end;
    this.end += cap;
    this.lastCap = cap;
    return start;
  }
  private lastCap = 0;

  /**
   * Writing a buffer the GPU may still be reading (bufferSubData) stalls
   * ANGLE/Metal on a CPU-GPU sync. All writes therefore go GPU-side:
   * zeros come from a shared zero buffer, data via a fresh staging buffer,
   * both with copyBufferSubData (queued, no CPU wait).
   */
  private zero(start: number, count: number): void {
    if (count <= 0) return;
    const gl = this.gl;
    gl.bindBuffer(gl.COPY_READ_BUFFER, zeroBuffer(gl, count * A_BYTES));
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, this.bufA);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, 0, start * A_BYTES, count * A_BYTES);
    gl.bindBuffer(gl.COPY_READ_BUFFER, null);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
  }

  private release(key: number): void {
    const s = this.slots.get(key);
    if (!s) return;
    this.slots.delete(key);
    this.zero(s.start, s.count);
    this.live -= s.cap;
    this.free.push({ start: s.start, cap: s.cap });
  }

  /** Write a chunk's pass mesh (positions already region-offset). */
  set(key: number, p: PassMesh | null, lx = 0, lz = 0): void {
    if (!p || p.vertexCount === 0) { this.release(key); this.updateBounds(); return; }
    const n = p.vertexCount;
    let s = this.slots.get(key);
    if (s && s.cap >= n && s.cap <= n * 3 + 256) {
      if (s.count > n) this.zero(s.start + n, s.count - n);
    } else {
      if (s) this.release(key);
      const cap = ((Math.ceil(n * 1.25) + 64) >> 2) << 2;
      const start = this.alloc(cap);
      s = { start, cap: this.lastCap, count: 0, minY: 0, maxY: 0, lx, lz, dist: 0 };
      this.slots.set(key, s);
      this.live += s.cap;
    }
    s.count = n; s.minY = p.minY; s.maxY = p.maxY;
    const gl = this.gl;
    // One fresh staging buffer (never in flight, so no sync) filled by a
    // single bufferData, then GPU-side copies into the region's buffers.
    const ab = n * A_BYTES, bb = n * B_BYTES, cb = n * C_BYTES;
    const st = staging(ab + bb + cb);
    st.set(new Uint8Array(p.a.buffer, p.a.byteOffset, ab), 0);
    st.set(new Uint8Array(p.b.buffer, p.b.byteOffset, bb), ab);
    st.set(new Uint8Array(p.c.buffer, p.c.byteOffset, cb), ab + bb);
    const stage = gl.createBuffer()!;
    gl.bindBuffer(gl.COPY_READ_BUFFER, stage);
    gl.bufferData(gl.COPY_READ_BUFFER, st, gl.STREAM_COPY, 0, ab + bb + cb);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, this.bufA);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, 0, s.start * A_BYTES, ab);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, this.bufB);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, ab, s.start * B_BYTES, bb);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, this.bufC);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, ab + bb, s.start * C_BYTES, cb);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
    gl.bindBuffer(gl.COPY_READ_BUFFER, null);
    gl.deleteBuffer(stage); // freed once the queued copies retire
    this.updateBounds();
  }

  private updateBounds(): void {
    this.sorted = null;
    let minY = Infinity, maxY = -Infinity, top = 0;
    for (const s of this.slots.values()) {
      if (s.minY < minY) minY = s.minY;
      if (s.maxY > maxY) maxY = s.maxY;
      if (s.start + s.cap > top) top = s.start + s.cap;
    }
    // trailing free space needs no drawing
    this.geometry.setDrawRange(0, (top >> 2) * 6);
    const visible = this.slots.size > 0;
    for (const m of this.meshes) m.visible = visible;
    if (!visible) return;
    const half = (REGION * CHUNK_SIZE) / 2;
    const halfY = (maxY - minY) / 2;
    const bs = this.geometry.boundingSphere!;
    bs.center.set(half, minY + halfY, half);
    bs.radius = Math.sqrt(2 * (half + 1) ** 2 + halfY * halfY) + 0.5;
  }

  get empty(): boolean { return this.slots.size === 0; }

  // --- per-chunk culling via WEBGL_multi_draw ------------------------------
  private sorted: Slot[] | null = null;

  /**
   * Make `mesh` draw only the chunks of this region inside the camera
   * frustum, as one multiDrawElements call. three.js routes a mesh flagged
   * isBatchedMesh through renderMultiDraw with these arrays; the chunk
   * shaders ignore the batching defines, and positions are region-local so
   * no per-instance matrices are needed.
   */
  /** Translucent: draw visible chunks back-to-front (no run merging). */
  sortBackToFront = false;

  attachCulling(mesh: THREE.Mesh, origin: THREE.Vector3): void {
    const m = mesh as unknown as MultiDrawMesh;
    m.isBatchedMesh = true;
    m._multiDrawStarts = new Int32Array(REGION * REGION * 4);
    m._multiDrawCounts = new Int32Array(REGION * REGION * 4);
    m._multiDrawCount = 0;
    m._multiDrawInstances = null;
    m._matricesTexture = null;
    m._indirectTexture = null;
    m._colorsTexture = null;
    m.colorTexture = null;
    mesh.onBeforeRender = (_r, _s, camera) => this.cull(m, camera, origin);
  }

  private cull(m: MultiDrawMesh, camera: THREE.Camera, origin: THREE.Vector3): void {
    const f = frustumFor(camera);
    if (!this.sorted) this.sorted = [...this.slots.values()].sort((a, b) => a.start - b.start);
    let n = 0;
    let runStart = -1, runEnd = 0; // vertex range of the current merged run
    const starts = m._multiDrawStarts, counts = m._multiDrawCounts;
    if (this.sortBackToFront) {
      _cam.setFromMatrixPosition(camera.matrixWorld);
      const vis = _vis; vis.length = 0;
      for (const s of this.sorted) {
        const lx = s.lx * CHUNK_SIZE, lz = s.lz * CHUNK_SIZE;
        _box.min.set(origin.x + lx - 1, s.minY - 1, origin.z + lz - 1);
        _box.max.set(origin.x + lx + CHUNK_SIZE + 1, s.maxY + 1, origin.z + lz + CHUNK_SIZE + 1);
        if (!f.intersectsBox(_box)) continue;
        const dx = origin.x + lx + 8 - _cam.x, dz = origin.z + lz + 8 - _cam.z;
        s.dist = dx * dx + dz * dz;
        vis.push(s);
      }
      vis.sort((a, b) => b.dist - a.dist);
      for (const s of vis) { starts[n] = (s.start >> 2) * 24; counts[n] = (s.count >> 2) * 6; n++; }
      m._multiDrawCount = n;
      return;
    }
    for (const s of this.sorted) {
      const lx = s.lx * CHUNK_SIZE, lz = s.lz * CHUNK_SIZE;
      _box.min.set(origin.x + lx - 1, s.minY - 1, origin.z + lz - 1);
      _box.max.set(origin.x + lx + CHUNK_SIZE + 1, s.maxY + 1, origin.z + lz + CHUNK_SIZE + 1);
      if (!f.intersectsBox(_box)) continue;
      if (runStart >= 0 && s.start === runEnd) {
        runEnd = s.start + s.cap; // adjacent slot: extend (its zeroed tail is degenerate)
        counts[n - 1] = ((runEnd - runStart) >> 2) * 6;
        continue;
      }
      runStart = s.start; runEnd = s.start + s.cap;
      starts[n] = (s.start >> 2) * 6 * 4; // byte offset into the u32 quad index buffer
      counts[n] = (s.cap >> 2) * 6;
      n++;
    }
    m._multiDrawCount = n;
  }

  dispose(): void {
    const gl = this.gl;
    for (const m of this.meshes) m.removeFromParent();
    // detach the SHARED index first: three deletes a disposed geometry's
    // index buffer, which would break every other region
    this.geometry.setIndex(null);
    this.geometry.dispose();
    if (this.bufA) { gl.deleteBuffer(this.bufA); gl.deleteBuffer(this.bufB); gl.deleteBuffer(this.bufC); }
    this.bufA = this.bufB = this.bufC = null;
  }
}

let stagingBuf = new Uint8Array(1 << 18);
function staging(bytes: number): Uint8Array {
  if (stagingBuf.length < bytes) stagingBuf = new Uint8Array(Math.max(bytes, stagingBuf.length * 2));
  return stagingBuf;
}

/** Shared zero-filled GPU buffer (bufferData without data zero-initialises). */
let zeroBuf: WebGLBuffer | null = null;
let zeroBytes = 0;
let zeroGl: WebGL2RenderingContext | null = null;
function zeroBuffer(gl: WebGL2RenderingContext, bytes: number): WebGLBuffer {
  if (zeroGl !== gl) { zeroBuf = null; zeroBytes = 0; zeroGl = gl; }
  if (!zeroBuf || zeroBytes < bytes) {
    if (zeroBuf) gl.deleteBuffer(zeroBuf);
    zeroBytes = Math.max(bytes, zeroBytes * 2, 1 << 20);
    zeroBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, zeroBuf);
    gl.bufferData(gl.COPY_WRITE_BUFFER, zeroBytes, gl.STATIC_COPY);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
  }
  return zeroBuf;
}

interface MultiDrawMesh {
  isBatchedMesh: boolean;
  _multiDrawStarts: Int32Array;
  _multiDrawCounts: Int32Array;
  _multiDrawCount: number;
  _multiDrawInstances: null;
  _matricesTexture: null;
  _indirectTexture: null;
  _colorsTexture: null;
  colorTexture: null;
}

const _box = new THREE.Box3();
const _cam = new THREE.Vector3();
const _vis: Slot[] = [];
const _frustum = new THREE.Frustum();
const _pv = new THREE.Matrix4();
/** Frustum of `camera` (cheap: one matrix multiply, called once per region draw). */
function frustumFor(camera: THREE.Camera): THREE.Frustum {
  return _frustum.setFromProjectionMatrix(_pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
}

export type BatchKind = 'opaque' | 'cutout' | 'translucent';

export interface BatchTarget {
  group: THREE.Object3D;
  shadowGroup: THREE.Object3D;
  material: (kind: BatchKind) => THREE.Material;
  /** Depth material for the shadow twin, or null (no shadow caster). */
  depthMaterial: (kind: BatchKind) => THREE.Material | null;
}

/** All regions of all passes. Keys are chunk world keys supplied by the caller. */
export class ChunkBatcher {
  private readonly regions = new Map<string, RegionPass>();
  private readonly index: QuadIndex;
  /** chunk key -> region keys it occupies (per pass) */
  private readonly owned = new Map<number, string[]>();

  /** Per-chunk culling inside regions (needs WEBGL_multi_draw). */
  private readonly multiDraw: boolean;

  constructor(private readonly gl: WebGL2RenderingContext, private readonly target: BatchTarget, cullChunks = true) {
    this.index = new QuadIndex(gl);
    this.multiDraw = cullChunks && gl.getExtension('WEBGL_multi_draw') !== null;
  }

  private region(kind: BatchKind, rx: number, rz: number): RegionPass {
    const k = kind + rx + ',' + rz;
    let r = this.regions.get(k);
    if (!r) {
      r = new RegionPass(this.gl, this.index);
      r.onIndexGrow = () => { for (const o of this.regions.values()) o.refreshIndex(); };
      const mesh = new THREE.Mesh(r.geometry, this.target.material(kind));
      mesh.position.set(rx * REGION * CHUNK_SIZE, 0, rz * REGION * CHUNK_SIZE);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.matrixWorld.copy(mesh.matrix);
      mesh.matrixWorldAutoUpdate = false;
      mesh.renderOrder = kind === 'translucent' ? 10 : kind === 'cutout' ? 5 : 0;
      r.sortBackToFront = kind === 'translucent';
      if (this.multiDraw) r.attachCulling(mesh, mesh.position);
      this.target.group.add(mesh);
      r.meshes.push(mesh);
      const dm = this.target.depthMaterial(kind);
      if (dm) {
        const caster = new THREE.Mesh(r.geometry, dm);
        caster.matrixAutoUpdate = false;
        caster.matrix.copy(mesh.matrix);
        caster.matrixWorld.copy(mesh.matrix);
        caster.matrixWorldAutoUpdate = false;
        if (this.multiDraw) r.attachCulling(caster, mesh.position);
        this.target.shadowGroup.add(caster);
        r.meshes.push(caster);
      }
      this.regions.set(k, r);
    }
    return r;
  }

  /** Upload (replace) a chunk's geometry for `kind`. Mutates p.a (adds the region offset). */
  set(key: number, cx: number, cz: number, kind: BatchKind, p: PassMesh | null): void {
    const rx = Math.floor(cx / REGION), rz = Math.floor(cz / REGION);
    const rk = kind + rx + ',' + rz;
    if (!p || p.vertexCount === 0) {
      const r = this.regions.get(rk);
      if (r) { r.set(key, null); this.dropIfEmpty(rk, r); }
      return;
    }
    const ox = (cx - rx * REGION) * CHUNK_SIZE * POS_SCALE;
    const oz = (cz - rz * REGION) * CHUNK_SIZE * POS_SCALE;
    if (ox !== 0 || oz !== 0) {
      const a = p.a;
      for (let o = 0, n = p.vertexCount * 4; o < n; o += 4) { a[o] += ox; a[o + 2] += oz; }
    }
    this.region(kind, rx, rz).set(key, p, cx - rx * REGION, cz - rz * REGION);
    let list = this.owned.get(key);
    if (!list) this.owned.set(key, (list = []));
    if (!list.includes(rk)) list.push(rk);
  }

  /** Remove every pass of a chunk. */
  remove(key: number): void {
    const list = this.owned.get(key);
    if (!list) return;
    for (const rk of list) {
      const r = this.regions.get(rk);
      if (r) { r.set(key, null); this.dropIfEmpty(rk, r); }
    }
    this.owned.delete(key);
  }

  private dropIfEmpty(rk: string, r: RegionPass): void {
    if (!r.empty) return;
    r.dispose();
    this.regions.delete(rk);
  }

  clear(): void {
    for (const r of this.regions.values()) r.dispose();
    this.regions.clear();
    this.owned.clear();
  }

  get regionCount(): number { return this.regions.size; }
}
